import * as React from 'react'
import { useQuery } from '@apollo/client'
import {
  EmptyState,
  CriticalIcon,
  LockIcon,
  Notice,
  Button,
  Skeleton,
  Link,
} from '@buffer-mono/popcorn'
import {
  BufferTrackerReact as BufferTracker,
  Client,
} from '@buffer-mono/tracking-plan'
import { graphql, readFragment } from '~publish/graphql'

import {
  PlanContent,
  PlanContent_Billing,
  LegacyPlanContent_Billing,
  OrganizationDataForBilling_AccountOrganization,
} from './BillingSettings/PlanContent'
import {
  PaymentDetails,
  PaymentDetails_Billing,
  PaymentDetails_Organization,
} from './BillingSettings/PaymentDetails'
import {
  BillingInformation,
  AddressAndTax_Billing,
} from './BillingSettings/BillingInformation'
import { Invoices } from './BillingSettings/Invoices'
import { MobileBilling } from './BillingSettings/MobileBilling'
import { NewSettingsPageLayout } from './components/NewSettingsPageLayout'
import { getMigrationHubURL } from '~publish/legacy/utils/formatters/getURL'
import { isLegacyBilling } from './BillingSettings/BillingSettings.helpers'

// eslint-disable-next-line @typescript-eslint/naming-convention
export const OrganizationPrivileges_AccountOrganization =
  graphql(/* GraphQL */ `
    fragment OrganizationPrivileges_AccountOrganization on AccountOrganization {
      canAddPaymentDetails
      privileges {
        canEdit
        canManageBilling
      }
    }
  `)

// eslint-disable-next-line @typescript-eslint/naming-convention
export const BillingFragment_AccountOrganization = graphql(
  /* GraphQL */ `
    fragment BillingFragment_AccountOrganization on AccountOrganization {
      billing {
        ... on OBBilling {
          ...PaymentDetails_Billing
          ...AddressAndTax_Billing
          ...PlanContent_Billing
        }
        ... on MPBilling {
          ...PaymentDetails_Billing
          ...AddressAndTax_Billing
          ...LegacyPlanContent_Billing
        }
      }
    }
  `,
  [
    PaymentDetails_Billing,
    AddressAndTax_Billing,
    LegacyPlanContent_Billing,
    PlanContent_Billing,
  ],
)

export const AccountForBillingQuery = graphql(
  /* GraphQL */ `
    query Account {
      account {
        id
        currentOrganization {
          billing {
            __typename
            ... on OBBilling {
              paymentDetails {
                paymentMethod
              }
              gateway {
                gatewayType
                gatewayPlatform
              }
              previousGateway {
                gatewayType
              }
              subscription {
                periodEnd
                isCanceledAtPeriodEnd
                canceledAt
                trial {
                  isActive
                }
                plan {
                  id
                  name
                  interval
                  limits {
                    members
                  }
                  prices {
                    basePlanPrice
                    baseMonthlyPrice
                  }
                }
              }
            }
          }
          members {
            totalCount
          }
          channels {
            isLocked
          }
          limits {
            channels
          }
          ...BillingFragment_AccountOrganization
          ...OrganizationDataForBilling_AccountOrganization
          ...OrganizationPrivileges_AccountOrganization
          ...PaymentDetails_Organization
          dismissedObjects {
            id
          }
        }
      }
    }
  `,
  [
    BillingFragment_AccountOrganization,
    OrganizationDataForBilling_AccountOrganization,
    OrganizationPrivileges_AccountOrganization,
    PaymentDetails_Organization,
  ],
)

export const Billing = (): JSX.Element => {
  const { data, loading, error, refetch } = useQuery(AccountForBillingQuery)

  // Refetch billing data when the billing updated event is triggered from the appshell
  React.useEffect(() => {
    const { ORGANIZATION_EVENT_KEY } = window?.appshell?.eventKeys || {}
    const { billingUpdated } = window?.appshell?.actionKeys || {}

    function handleBillingUpdate({
      detail,
    }: {
      detail: { action: string }
    }): void {
      if (detail.action === billingUpdated?.toString()) {
        void refetch()
      }
    }

    window.addEventListener(ORGANIZATION_EVENT_KEY, handleBillingUpdate)

    return function cleanup(): void {
      window.removeEventListener(ORGANIZATION_EVENT_KEY, handleBillingUpdate)
    }
  }, [refetch])

  const billingData = data?.account?.currentOrganization
    ? readFragment(
        BillingFragment_AccountOrganization,
        data.account.currentOrganization,
      ).billing
    : null

  const orgPrivileges = data?.account?.currentOrganization
    ? readFragment(
        OrganizationPrivileges_AccountOrganization,
        data.account.currentOrganization,
      )
    : null

  const organizationData = data?.account?.currentOrganization ?? null
  const shouldShowLegacyWarning = billingData && isLegacyBilling(billingData)

  const BillingPageWrapper = ({
    children,
  }: {
    children: React.ReactNode
  }): JSX.Element => (
    <NewSettingsPageLayout title="Billing">{children}</NewSettingsPageLayout>
  )

  if (error) {
    return (
      <BillingPageWrapper>
        <EmptyState variant="critical">
          <EmptyState.Icon>
            <CriticalIcon />
          </EmptyState.Icon>
          <EmptyState.Heading>
            Failed to load billing information
          </EmptyState.Heading>
          <EmptyState.Description>
            Something went wrong. Please try again later.
          </EmptyState.Description>
        </EmptyState>
      </BillingPageWrapper>
    )
  }

  if (loading) {
    return (
      <BillingPageWrapper>
        <Skeleton width="100%" height="250px" />
        <Skeleton width="100%" height="75px" />
        <Skeleton width="100%" height="150px" />
        <Skeleton width="100%" height="150px" />
      </BillingPageWrapper>
    )
  }

  if (orgPrivileges?.privileges?.canEdit === false) {
    return (
      <BillingPageWrapper>
        <EmptyState>
          <EmptyState.Icon>
            <LockIcon />
          </EmptyState.Icon>
          <EmptyState.Heading>
            Contact an admin to manage billing
          </EmptyState.Heading>
          <EmptyState.Description>
            Need help?{' '}
            <Link
              as="button"
              onClick={(): void => {
                window.Brainfish?.Widgets?.open()
              }}
            >
              Talk to our support team
            </Link>
          </EmptyState.Description>
        </EmptyState>
      </BillingPageWrapper>
    )
  }

  const billing = data?.account?.currentOrganization?.billing

  if (
    billing?.__typename === 'OBBilling' &&
    billing?.gateway?.gatewayType === 'revenuecat'
  ) {
    return (
      <BillingPageWrapper>
        <MobileBilling data={data} />

        {/* If the user's previous billing gateway was Stripe, let's fetch the old invoices */}
        {billing?.previousGateway?.gatewayType === 'stripe' && (
          <Invoices billingData={billingData} />
        )}
      </BillingPageWrapper>
    )
  }

  return (
    <BillingPageWrapper>
      {/* Notices */}
      {shouldShowLegacyWarning && (
        <Notice variant="warning">
          <Notice.Text>
            You are on a legacy plan, update your plan to unlock all the latest
            features.
          </Notice.Text>
          <Notice.Actions>
            <Button
              as={'a'}
              href={getMigrationHubURL()}
              target="_blank"
              variant="warning"
              size="small"
              onClick={(): void => {
                BufferTracker.cTAClicked({
                  cta: 'publish-billing-legacyPlan-updateNow',
                  clientName: Client.PublishWeb,
                  upgradePathName: 'legacyBillingUpdateNow-migrate',
                  product: 'publish',
                })
              }}
            >
              Update Now
            </Button>
          </Notice.Actions>
        </Notice>
      )}
      {/* Plan Content */}
      <PlanContent
        billingData={billingData}
        organizationData={organizationData}
      />

      {/* Payment Method Details */}
      <PaymentDetails
        canAddPaymentDetails={orgPrivileges?.canAddPaymentDetails ?? false}
        data={billingData}
        organizationData={organizationData}
      />

      {/* Billing information */}
      <BillingInformation data={billingData} />

      {/* Invoices */}
      <Invoices billingData={billingData} />
    </BillingPageWrapper>
  )
}

export default Billing
